import { Dropdown } from 'antd';
import {
	CopyOutlined,
	EditOutlined,
	HistoryOutlined,
} from '@ant-design/icons';

import { VSCPartData } from '../../../interfaces/VersaBuilt.Interface';
import RobotProgramViewModal from './RobotProgramViewModal';
import RobotProgramEditModal from './RobotProgramEditModal';
import RobotProgramHistoryModal from './RobotProgramHistoryModal';
import RobotProgramDuplicateModal from './RobotProgramDuplicateModal';

export type RobotProgramAction =
	| 'view'
	| 'edit'
	| 'duplicate'
	| 'history'
	/** Opens the View form on an unsaved template; Save creates the program. */
	| 'create';

export interface OpenRobotProgram {
	action: RobotProgramAction;
	program: VSCPartData;
	orderLineItemId: string;
	/** The line item's other programs, so Duplicate can reject a taken name. */
	siblings: VSCPartData[];
}

interface RobotProgramActionsButtonProps {
	onAction: (action: RobotProgramAction) => void;
	disabled?: boolean;
	loading?: boolean;
}

/** "View" plus a menu of the other actions, shared by every robot program list. */
export const RobotProgramActionsButton = ({
	onAction,
	disabled,
	loading,
}: RobotProgramActionsButtonProps) => (
	<Dropdown.Button
		loading={loading}
		onClick={() => onAction('view')}
		menu={{
			items: [
				{
					key: 'edit',
					label: 'Edit JSON',
					icon: <EditOutlined />,
					disabled,
					onClick: () => onAction('edit'),
				},
				{
					key: 'duplicate',
					label: 'Duplicate',
					icon: <CopyOutlined />,
					disabled,
					onClick: () => onAction('duplicate'),
				},
				{
					key: 'history',
					label: 'History',
					icon: <HistoryOutlined />,
					onClick: () => onAction('history'),
				},
			],
		}}
	>
		View
	</Dropdown.Button>
);

interface RobotProgramModalsProps {
	open: OpenRobotProgram | null;
	onClose: () => void;
	readOnly?: boolean;
	useInches: boolean;
	onChangeUnits: (useInches: boolean) => void;
}

export const RobotProgramModals = ({
	open,
	onClose,
	readOnly,
	useInches,
	onChangeUnits,
}: RobotProgramModalsProps) => {
	const programFor = (action: RobotProgramAction) =>
		open?.action === action ? open.program : null;
	const orderLineItemId = open?.orderLineItemId ?? '';

	return (
		<>
			<RobotProgramViewModal
				orderLineItemId={orderLineItemId}
				program={programFor('view') ?? programFor('create')}
				isNew={open?.action === 'create'}
				siblings={open?.siblings}
				readOnly={readOnly}
				useInches={useInches}
				onChangeUnits={onChangeUnits}
				onClose={onClose}
			/>
			<RobotProgramEditModal
				orderLineItemId={orderLineItemId}
				program={programFor('edit')}
				useInches={useInches}
				onChangeUnits={onChangeUnits}
				onClose={onClose}
			/>
			<RobotProgramHistoryModal
				program={programFor('history')}
				useInches={useInches}
				onChangeUnits={onChangeUnits}
				onClose={onClose}
			/>
			<RobotProgramDuplicateModal
				orderLineItemId={orderLineItemId}
				program={programFor('duplicate')}
				programs={open?.siblings ?? []}
				onClose={onClose}
			/>
		</>
	);
};
